/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { v4 } from "uuid";
import { Box, Spacer, Table, Text } from "@nordcloud/gnui";
import { Breadcrumbs, showSuccess } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditDropdown } from "~/components/InlineEdit/InlineEditDropdown";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { InlinePrerequisitesProduct } from "~/components/Product/InlinePrerequisitesProduct";
import { AddSLA } from "~/components/SLA/AddSLA";
import { SLAItem } from "~/components/SLA/SLAItem";
import { DetailRow } from "~/components/Utils/DetailRow";
import { dateFormatWithHours } from "~/constants";
import { useEmployees } from "~/hooks/useEmployees";
import { useSla } from "~/hooks/useSla";
import {
  PrerequisiteList,
  PrerequisitesItem,
  ProductIdRouteParams,
  Sla,
} from "~/models";
import { ACTIONS, Can, useAccess } from "~/routing";
import {
  checkValue,
  generateActionSuccessText,
  personToDropdown,
  useAxios,
} from "~/tools";
import { getEmployeeStatusColor } from "~/tools/display/displayEmployeeColor";
import { useProduct } from "./hooks/useProduct";
import { productInitialLoadingState } from "./initialState";
import { ProductLoading, ProductSubmitValues } from "./types";

function ProductDetails() {
  const state = useParams<ProductIdRouteParams>();
  const { productId } = state;

  const [list, setList] = React.useState<PrerequisiteList>([]);
  const [isLoading, setIsLoading] = React.useState<ProductLoading>(
    productInitialLoadingState
  );

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const [
    isPrerequisitesEditMode,
    setIsPrerequisitesEditMode,
  ] = React.useState<boolean>(false);

  const { hasAccess: canEdit } = useAccess(ACTIONS.EDIT_PRODUCT_CATALOG);

  const { product, fetchProduct } = useProduct();
  const { sla, fetchSLA } = useSla();
  const { employees } = useEmployees();

  const addIdPrerequisites = React.useCallback(() => {
    const mapped = product?.prerequisites?.map((text: PrerequisitesItem) => {
      return { id: v4(), ...text };
    });
    product?.prerequisites && setList(mapped);
  }, [product?.prerequisites]);

  React.useEffect(() => {
    addIdPrerequisites();
  }, [addIdPrerequisites]);

  const breadcrumbLabels = product
    ? [{ value: product?.id, label: product?.name }]
    : undefined;

  const onSubmit = async (values: ProductSubmitValues) => {
    const ownerId =
      checkValue(values.ownerId) || checkValue(product?.owner?.id);
    const organizeValues = { ...values, ownerId };

    try {
      await Axios().put(`/catalog/products/${productId}`, organizeValues);
      showSuccess(generateActionSuccessText("Product")()("added")());
      fetchProduct();
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(productInitialLoadingState);
    }
  };

  const pricingTooltip = "Link to spreadsheet with pricing formula";

  return (
    <Container>
      <Breadcrumbs title={product?.name || ""} labels={breadcrumbLabels}>
        <Can action={ACTIONS.ADD_PRODUCT_SLA}>
          <AddSLA id={productId} endpoint="products" refetch={fetchSLA} />
        </Can>
      </Breadcrumbs>

      <Box spacing="spacing04">
        <Row>
          <Col xs={8} sm={8} md={4} lg={5}>
            <Text weight="medium">Product Details</Text>
            <DetailRow title="Name" minWidth="7rem">
              <InlineEditText
                name="name"
                defaultValue={product.name}
                onSubmit={(name) => onSubmit({ ...product, name })}
                canEdit={canEdit}
              />
            </DetailRow>

            <DetailRow title="Owner" minWidth="7rem">
              <InlineEditDropdown
                name="ownerId"
                defaultValue={product.owner?.id || ""}
                options={employees.map(personToDropdown)}
                onSubmit={(ownerId) => onSubmit({ ...product, ownerId })}
                canEdit={canEdit}
                color={getEmployeeStatusColor(
                  employees.filter((emp) => emp.id === product.owner?.id)[0]
                )}
              />
            </DetailRow>
          </Col>

          <Col xs={8} sm={8} md={4} lg={7}>
            <Box css={{ marginTop: "auto" }} boxStyle="lightGrey">
              <Text size="sm" weight="medium">
                History of Editing
              </Text>
              <Table striped>
                <Table.tbody>
                  <Table.tr>
                    <Table.td>Created by:</Table.td>
                    <Table.td data-cy="product-created-author">
                      {product?.log?.created?.author}
                    </Table.td>
                    <Table.td data-cy="product-created-time">
                      {dayjs(product?.log?.created?.time).format(
                        `${dateFormatWithHours}`
                      )}
                    </Table.td>
                  </Table.tr>
                  <Table.tr>
                    <Table.td>Modified by:</Table.td>
                    <Table.td data-cy="product-modify-author">
                      {product?.log?.modified?.author}
                    </Table.td>
                    <Table.td data-cy="product-modify-time">
                      {dayjs(product?.log?.modified?.time).format(
                        `${dateFormatWithHours}`
                      )}
                    </Table.td>
                  </Table.tr>
                </Table.tbody>
              </Table>
            </Box>
          </Col>
        </Row>
        <Spacer height="1rem" />

        <Row>
          <Col>
            <DetailRow
              title="Pricing"
              tooltip={pricingTooltip}
              minWidth="12rem"
            >
              <InlineEditText
                name="pricing"
                defaultValue={product.pricing}
                link
                onSubmit={(pricing) => onSubmit({ ...product, pricing })}
                canEdit={canEdit}
              />
            </DetailRow>

            <DetailRow title="SalesForce product link" minWidth="12rem">
              <InlineEditText
                name="sf_product_link"
                defaultValue={product.sfProductLink}
                link
                onSubmit={(sfProductLink) =>
                  onSubmit({ ...product, sfProductLink })
                }
                canEdit={canEdit}
              />
            </DetailRow>

            <DetailRow title="Description" minWidth="12rem">
              <InlineEditText
                textArea
                name="description"
                defaultValue={product.description}
                onSubmit={(description) =>
                  onSubmit({ ...product, description })
                }
                canEdit={canEdit}
              />
            </DetailRow>
          </Col>
        </Row>
      </Box>

      {sla.length !== 0 && (
        <Box spacing="spacing04" data-cy="product-sla">
          <Text weight="medium">SLA</Text>
          {sla?.map((s: Sla) => (
            <SLAItem key={s?.id} sla={s} />
          ))}
        </Box>
      )}
      <InlinePrerequisitesProduct
        item={product}
        property="prerequisites"
        onSubmit={onSubmit}
        list={list}
        setList={setList}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isPrerequisitesEditMode={isPrerequisitesEditMode}
        setIsPrerequisitesEditMode={setIsPrerequisitesEditMode}
      />
    </Container>
  );
}

export { ProductDetails };
